import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { vendorAPI, invoiceAPI } from '../api/api';
import { getColorForName } from '../utils/colorUtils';

const StackedVendorCostChart = () => {
  const [timeRange, setTimeRange] = useState('yearly');
  const [chartData, setChartData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Format date for display (e.g., "Jan 2023")
  const formatDateLabel = useCallback((date) => {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, []);

  // Generate the last 12 months from current date
  const generateLast12Months = useCallback(() => {
    const months = [];
    
    // Start from 11 months ago to include the current month (12 months total)
    // To avoid month-end date issues, we set the day to 1 before adjusting months
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setDate(1); // Set to day 1 to avoid month overflow issues
      date.setMonth(date.getMonth() - i);
      months.push(formatDateLabel(date));
    }
    
    return months;
  }, [formatDateLabel]);

  // Process the invoice data to group by vendors and months
  const processInvoiceData = useCallback((invoices, allVendors) => {
    // Create a map of vendor IDs to vendor names (prefer standard_name if available)
    const vendorMap = {};
    allVendors.forEach(vendor => {
      // Use standard_name if available and not empty, otherwise use name
      vendorMap[vendor.id] = vendor.standard_name && vendor.standard_name.trim() !== '' 
        ? vendor.standard_name 
        : vendor.name;
    });

    // Group data by month
    const monthlyData = {};
    
    invoices.forEach(invoice => {
      if (!invoice.invoice_date || !invoice.vendor_id || !invoice.total_amount) {
        return; // Skip invalid invoices
      }
      
      // Format the date to 'YYYY-MM' format for grouping by month
      const date = new Date(invoice.invoice_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // If this month doesn't exist in our data, initialize it
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: formatDateLabel(date) };
      }
      
      // Get the vendor name for this invoice (use standard name if available)
      const vendorName = vendorMap[invoice.vendor_id] || `Vendor ${invoice.vendor_id}`;
      
      // Add the cost for this vendor to this month
      if (monthlyData[monthKey][vendorName]) {
        monthlyData[monthKey][vendorName] += parseFloat(invoice.total_amount) || 0;
      } else {
        monthlyData[monthKey][vendorName] = parseFloat(invoice.total_amount) || 0;
      }
    });

    // Convert to array and sort by month
    let result = Object.values(monthlyData);
    result.sort((a, b) => new Date(a.month) - new Date(b.month));

    // Generate the last 12 months from current date
    const last12Months = generateLast12Months();

    // Merge with actual data to ensure all months are represented
    const mergedData = last12Months.map(monthLabel => {
      const existingMonth = result.find(item => item.month === monthLabel);
      if (existingMonth) {
        return existingMonth;
      } else {
        // Create an empty month with all vendors set to 0 (using standard names)
        const emptyMonth = { month: monthLabel };
        allVendors.forEach(vendor => {
          const vendorName = vendor.standard_name && vendor.standard_name.trim() !== '' 
            ? vendor.standard_name 
            : vendor.name;
          emptyMonth[vendorName] = 0;
        });
        return emptyMonth;
      }
    });

    return mergedData;
  }, [generateLast12Months, formatDateLabel]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch both cost data and vendors
        const [invoiceResponse, vendorResponse] = await Promise.all([
          invoiceAPI.getInvoices(0, 1000), // Get all invoices to group by vendors and months
          vendorAPI.getVendors()
        ]);
        
        setVendors(vendorResponse.data || []);
        
        // Process the invoice data to group by vendors and months
        const processedData = processInvoiceData(invoiceResponse.data || [], vendorResponse.data || []);
        setChartData(processedData);
      } catch (error) {
        console.error('Error fetching stacked vendor cost data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, processInvoiceData]);

  // Calculate vendor-wise price distribution for the pie chart
  const calculateVendorDistribution = useCallback(() => {
    if (!chartData || chartData.length === 0) return [];
    
    const vendorTotals = {};
    
    chartData.forEach(monthData => {
      Object.keys(monthData).forEach(key => {
        if (key !== 'month') { // Skip the month key
          if (vendorTotals[key]) {
            vendorTotals[key] += monthData[key] || 0;
          } else {
            vendorTotals[key] = monthData[key] || 0;
          }
        }
      });
    });
    
    // Convert to array and sort by value (descending)
    return Object.entries(vendorTotals)
      .map(([name, value]) => ({
        name,
        value: parseFloat(value) || 0
      }))
      .filter(item => item.value > 0) // Filter out vendors with 0 total
      .sort((a, b) => b.value - a.value); // Sort by value descending
  }, [chartData]);

  // Get all vendor names for the legend and bars (prefer standard_name if available)
  const vendorNames = vendors.map(vendor => 
    vendor.standard_name && vendor.standard_name.trim() !== '' 
      ? vendor.standard_name 
      : vendor.name
  );

  const vendorDistributionData = calculateVendorDistribution();

  return (
    <Card 
      elevation={0} 
      sx={{ 
        p: 3, 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        height: '100%',
        boxShadow: 1
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Yearly Cost Overview
        </Typography>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
            disabled // Disable since we're always showing last 12 months
          >
            <MenuItem value="yearly">Last 12 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ height: 600, display: 'flex', flexDirection: 'row' }}> {/* Increased height and added flex layout */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flex={1}>
            <CircularProgress />
          </Box>
        ) : chartData.length > 0 ? (
          <>
            {/* Stack Plot Section - Left part */}
            <Box sx={{ width: '70%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 80, // Increased bottom margin to accommodate rotated labels
                  }}
                  barCategoryGap={0}
                  barGap={0}
                  stackOffset="none"
                >
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                  <XAxis 
                    dataKey="month" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80} // Increased height for rotated labels
                    tick={{ fontSize: 11, fill: '#666' }}
                  />
                  <YAxis 
                    tick={{ fill: '#666' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value.toFixed(2)}`, name]}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    height={40}
                    wrapperStyle={{ paddingTop: '20px' }}
                  />
                  {vendorNames.map((vendorName, index) => (
                    <Bar 
                      key={vendorName}
                      dataKey={vendorName} 
                      stackId="a"
                      name={vendorName}
                      fill={getColorForName(vendorName, vendorNames.map(name => ({name})), index)}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
            
            {/* Pie Chart Section - Right part */}
            <Box sx={{ width: '30%', pl: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                Vendor Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={vendorDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} // Only show percentage to avoid crowding
                  >
                    {vendorDistributionData.map((entry, index) => {
                      return <Cell key={`cell-${index}`} fill={getColorForName(entry.name, vendorDistributionData, index)} />;
                    })}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Amount']}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flex={1}>
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default StackedVendorCostChart;